import React from 'react';
import { Link } from 'react-router-dom';
import {
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  Instagram,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
} from 'lucide-react';

/* ---------------- FOOTER DATA (edit paths to match your routes) ---------------- */

const LINK_COLUMNS = [
  {
    title: 'Products',
    links: [
      { label: 'Education ERP', to: '/Educationerp' },
      { label: 'Hospital ERP', to: '/Hospitalerp' },
      { label: 'HRMS', to: '/HRMS' },
      { label: 'CRM', to: '/CRM' },
      { label: 'Inventory ERP', to: '/Inventoryerp' },
      { label: 'Custom ERP', to: '/Customerp' },
      { label: 'All Products', to: '/products' },
    ],
  },
  {
    title: 'Services',
    links: [
      { label: 'Software Development', to: '/softwaredevelopment' },
      { label: 'Mobile App Development', to: '/mobileappdevelopment' },
      { label: 'Web Development', to: '/webdevelopment' },
      { label: 'UI/UX Design', to: '/uiuxDesign' },
      { label: 'Cloud Solutions', to: '/cloudsolutions' },
      { label: 'AI Solutions', to: '/aiautomation' },
    ],
  },
  {
    title: 'Industries',
    links: [
      { label: 'Education', to: '/education' },
      { label: 'Healthcare', to: '/healthcare' },
      { label: 'Manufacturing', to: '/manufacturing' },
      { label: 'Retail & POS', to: '/retail' },
      { label: 'Logistics & Transport', to: '/logistics' },
      { label: 'View All Industries', to: '/industries' },
    ],
  },
  {
    title: 'Solutions',
    links: [
      { label: 'Business Solutions', to: '/BusinessOperations' },
      { label: 'Management Systems', to: '/ManagementSystems' },
      { label: 'AI & Automation Solutions', to: '/AiAutomationsolutions' },
      { label: 'Customer Engagement ', to: '/customerengagement' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', to: '/about-us' },
      { label: 'Our Team', to: '/about-us' },
      // { label: 'Careers', to: '/careers' },
      { label: 'Blog', to: '/blog' },
      { label: 'Contact Us', to: '/contact-us' },
    ],
  },
];

// 1. UPDATE THESE LINKS WITH YOUR ACTUAL SOCIAL MEDIA URLs
const SOCIALS = [
  { icon: Facebook, label: 'Facebook', href: 'https://facebook.com/yourpage' },
  { icon: Linkedin, label: 'LinkedIn', href: 'https://linkedin.com/company/yourcompany' },
  { icon: Twitter, label: 'Twitter', href: 'https://twitter.com/yourhandle' },
  { icon: Youtube, label: 'YouTube', href: 'https://youtube.com/c/yourchannel' },
  { icon: Instagram, label: 'Instagram', href: 'https://instagram.com/yourprofile' },
];

const Footer = () => {
  // Matching the exact container logic from Navbar for perfect alignment
  const containerClasses = "w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8";

  return (
    <footer className="bg-[#0A1B3D] border-t border-white/10 pt-12 pb-6 mt-auto">
      <div className={containerClasses}>

        {/* Top Section: 1 col (mobile) -> 3 col (tablet) -> 7 col fluid (desktop) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-[1.7fr_repeat(5,1fr)_1.5fr] gap-x-8 gap-y-10 mb-10">

          {/* Brand column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1 flex flex-col items-start">
            <Link to="/" className="flex items-center mb-5 group">
              <img
                src="/smartedzmain.png"
                alt="SmartEdz Logo"
                className="h-14 sm:h-16 w-auto object-contain"
              />
              <span
                className="text-2xl sm:text-3xl font-bold tracking-tight leading-none relative z-50 ml-2 sm:ml-4"
                style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
              >
                <span className="bg-gradient-to-t from-[#18469A] via-[#1B83C5] to-[#21BEE1] bg-clip-text text-transparent">Smart</span><span className="bg-gradient-to-t from-[#E35D14] to-[#F4A51D] bg-clip-text text-transparent">Edz</span>
              </span>
            </Link>

            <p className="text-sm text-slate-400 leading-relaxed max-w-xs mb-6">
              SmartEdz Technologies is a leading software development company delivering innovative ERP, web, mobile &amp; AI solutions for businesses across the globe.
            </p>

            {/* Social icons */}
            <div className="flex flex-wrap gap-3">
              {SOCIALS.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank" // Opens in a new tab
                  rel="noopener noreferrer" // Security best practice for target="_blank"
                  aria-label={label}
                  className="w-9 h-9 rounded-full border border-slate-600 flex items-center justify-center text-slate-300 hover:bg-primary hover:border-primary hover:text-white transition-all hover:scale-110"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {LINK_COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="text-[15px] font-semibold text-white mb-5">{col.title}</h3>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-slate-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact column */}
          <div>
            <h3 className="text-[15px] font-semibold text-white mb-5">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a href="tel:+916399191691" className="flex items-center gap-3 text-sm text-slate-400 hover:text-white transition-colors group">
                  <span className="w-9 h-9 rounded-full border border-slate-600 flex items-center justify-center shrink-0 group-hover:border-primary transition-colors">
                    <Phone className="w-4 h-4 text-primary" />
                  </span>
                  +91 63991 91691
                </a>
              </li>
              <li>
                <a href="mailto:smartedzhyd@gmail.com" className="flex items-center gap-3 text-sm text-slate-400 hover:text-white transition-colors group">
                  <span className="w-9 h-9 rounded-full border border-slate-600 flex items-center justify-center shrink-0 group-hover:border-primary transition-colors">
                    <Mail className="w-4 h-4 text-primary" />
                  </span>
                  smartedzhyd@gmail.com
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-sm text-slate-400">
                  <span className="w-9 h-9 rounded-full border border-slate-600 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-primary" />
                  </span>
                  Hyderabad, Telangana, India
                </div>
              </li>
            </ul>

            <Link
              to="/contact-us"
              className="mt-6 inline-flex items-center gap-2 border border-slate-600 rounded-lg px-5 py-2.5 text-sm font-medium text-white hover:bg-primary hover:border-primary transition-all"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

        </div>

        {/* Bottom Section */}
        <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} SmartEdz LLP. All Rights Reserved.
          </p>

          <div className="flex items-center gap-4 text-sm text-slate-400">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <span className="text-slate-600">|</span>
            <Link to="/TermsConditions" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;